import { useState, useEffect, useRef, useCallback } from "react";
import "./ProgressiveList.css";

// ================================
// PROGRESSIVE LIST LOADING
// Replaces the numbered per-page pagination on Read Blogs, Profile and
// Author Profile. The page shows the first `step` items; when the reader
// scrolls near the end, a "Loading more…" indicator appears and the next
// `step` items are added below. Positions stay whole-list (1, 2, 3 … N), so
// Smart Links and sponsored-ad slots keep exactly the same numbering.
//
// `resetKey` — when it changes (a new search/filter, a different author)
// the list goes back to its first batch, as "back to page 1" used to.
// ================================

const LOAD_DELAY_MS = 250;
const ROOT_MARGIN = "300px 0px";

export function useProgressiveList(items, step, resetKey) {
  const total = Array.isArray(items) ? items.length : 0;
  const [count, setCount] = useState(step);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sentinel, setSentinel] = useState(null);
  const timerRef = useRef(null);
  const busyRef = useRef(false);

  const hasMore = count < total;

  // Back to the first batch whenever the list itself is replaced.
  useEffect(() => {
    clearTimeout(timerRef.current);
    busyRef.current = false;
    setLoadingMore(false);
    setCount(step);
  }, [resetKey, step]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const loadMore = useCallback(() => {
    if (busyRef.current) return;
    busyRef.current = true;
    setLoadingMore(true);
    timerRef.current = setTimeout(() => {
      setCount((c) => c + step);
      setLoadingMore(false);
      busyRef.current = false;
    }, LOAD_DELAY_MS);
  }, [step]);

  // Watch the sentinel below the list. The observer is re-created after every
  // batch so that, if the sentinel is STILL on screen (short cards, tall
  // screen), its first callback loads the next batch too.
  useEffect(() => {
    if (!sentinel || !hasMore || loadingMore) return undefined;
    if (typeof window === "undefined" || typeof window.IntersectionObserver !== "function") {
      return undefined;
    }
    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { rootMargin: ROOT_MARGIN }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinel, hasMore, loadingMore, count, loadMore]);

  return {
    visibleItems: (items || []).slice(0, count),
    step,
    hasMore,
    loadingMore,
    loadMore,
    sentinelRef: setSentinel,
  };
}

// The element under the list: the scroll sentinel, the "Loading more…"
// indicator, and a quiet end-of-list note. Renders nothing for an empty list.
export default function ListLoader({ list, total, endLabel = "You're all caught up ✨" }) {
  if (!total) return null;
  const { hasMore, loadingMore, loadMore, sentinelRef, step } = list;
  const noObserver =
    typeof window === "undefined" || typeof window.IntersectionObserver !== "function";

  return (
    <div className="plist-loader" ref={sentinelRef} aria-live="polite">
      {loadingMore ? (
        <div className="plist-loading" role="status">
          <span className="plist-spinner" aria-hidden="true" />
          <span>Loading more…</span>
        </div>
      ) : hasMore ? (
        noObserver && (
          <button type="button" className="plist-more-btn" onClick={loadMore}>
            Show more
          </button>
        )
      ) : (
        total > step && <p className="plist-end">{endLabel}</p>
      )}
    </div>
  );
}
